import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './scss/language-switcher.component.scss';
import { ReactComponent as CnFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-china.svg';
import { ReactComponent as FrFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-france.svg';
import { ReactComponent as EsFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-spain.svg';
import { ReactComponent as EnFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-united-states.svg';
import { ReactComponent as ItFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-italy.svg';
import { ReactComponent as DeFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-germany.svg';
import { ReactComponent as SlFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-slovenia.svg';
import { ReactComponent as PtFlagIcon } from '../../static/images/flag-svg/emojione_flag-for-portugal.svg';
import { ReactComponent as ArrowUpIcon } from '../../static/images/svg/iconamoon_arrow-up-2-fill.svg';
import { Typography } from '@mui/material';
import { userData } from '../../page/app.page';
import { Resources } from '../../i18n';
import { COLOR_KEY, getModeColor } from '../../themes/customization.value';
import { GetThemeMode } from '../../themes/tool';


// 模拟国旗图标
const Flag = ({ code }: { code: string }) => {
  switch (code) {
    case 'IT':
      return <ItFlagIcon />;
    case 'CN':
      return <CnFlagIcon />;
    case 'FR':
      return <FrFlagIcon />;
    case 'EN':
      return <EnFlagIcon />;
    case 'SP':
      return <EsFlagIcon />;
    case 'DE':
      return <DeFlagIcon />;
    case 'SL':
      return <SlFlagIcon />;
    case 'PT':
      return <PtFlagIcon />;
    default:
      return null;
  }
};


const languages = [
  { code: 'IT', name: 'IT', flag: <ItFlagIcon /> },
  { code: 'CN', name: '中文', flag: <CnFlagIcon /> },
  // { code: 'FR', name: 'FR', flag: <FrFlagIcon /> },
  { code: 'EN', name: 'EN', flag: <EnFlagIcon /> },
  { code: 'SP', name: 'ES', flag: <EsFlagIcon /> },
  // { code: 'DE', name: 'DE', flag: <DeFlagIcon /> },
  // { code: 'SL', name: 'SL', flag: <SlFlagIcon /> },
  // { code: 'PT', name: 'PT', flag: <PtFlagIcon /> },
] as const;

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  // ref 用来检测点击是否在组件外部
  const switcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedLanguage(i18n.language);
  }, [i18n.language]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  const changeLanguage = (lng: Resources) => {
    userData.updateLanguage(lng);
    setSelectedLanguage(lng);
    setIsOpen(false);
  };

  const getLangName = (code: string) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : code.toUpperCase();
  }
  const themeMode = GetThemeMode();
  const getColor = (key: COLOR_KEY) => getModeColor(key, themeMode);
  return (
    <div className="language-switcher" ref={switcherRef}>
      <div className="selected-language" onClick={() => setIsOpen(!isOpen)}>
        <Flag code={selectedLanguage} />
        <Typography variant="subtitle1" sx={{ color: getColor(COLOR_KEY.textPrimary300) }}>
          {getLangName(selectedLanguage)}
        </Typography>
        <ArrowUpIcon className={`arrow ${isOpen ? 'up' : 'down'}`} />
      </div>
      {isOpen && (
        <ul className="language-list">
          {languages.map(language => (
            <li key={language.code} onClick={() => changeLanguage(language.code)} className={selectedLanguage === language.code ? 'selected' : ''}>
              <Flag code={language.code} />
              <Typography variant="subtitle1" className='language-name'>
                {language.name}
              </Typography>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;